import React, { useState } from "react";
import { AiOutlineDatabase, AiOutlineFilter, AiOutlineSearch } from "react-icons/ai";

const SearchBar = ({
  searchQuery,
  handleSearch,
  handleSearchClick,
  selectedModel,
  handleModelChange,
  setLimit,
  searchType,
  setSearchType,
  setSearchQuery,
  loading: externalLoading,
}) => {
  const [customLimit, setCustomLimit] = useState(5);
  const [questionId, setQuestionId] = useState("");
  const [id, setId] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [loading, setLoading] = useState(false);

  const handleCustomLimitChange = (event) => {
    const value = event.target.value;
    setErrorMessage(Number(value) < 1 ? "Limit must be a positive number." : "");
    setCustomLimit(value);
  };

  const handleSearchButtonClick = async () => {
    let search = "";

    if (searchType === "questionId") {
      if (!questionId) {
        setErrorMessage("Question ID cannot be empty.");
        return;
      }
      if (!/^[0-9a-fA-F]{24}$/.test(questionId)) {
        setErrorMessage("Invalid Question ID format.");
        return;
      }
      search = questionId;
    } else if (searchType === "id") {
      if (!id) {
        setErrorMessage("ID cannot be empty.");
        return;
      }
      if (Number.isNaN(Number(id)) || !Number.isInteger(Number(id))) {
        setErrorMessage("ID must be a valid number.");
        return;
      }
      search = id;
    } else {
      search = searchQuery;
    }

    setSearchQuery(search || "");
    const nextLimit = Number(customLimit) || 5;
    setLimit(nextLimit);
    setErrorMessage("");
    setLoading(true);

    try {
      await handleSearchClick(search, nextLimit);
    } catch (error) {
      setErrorMessage("Search failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const busy = loading || externalLoading;

  return (
    <section className="mb-6 rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
      <div className="mb-4 flex flex-col justify-between gap-3 sm:flex-row sm:items-center">
        <div>
          <h2 className="text-lg font-bold text-slate-950">Find Questions</h2>
          <p className="text-sm text-slate-500">Filter by title, question ID, numeric ID, model, and page size.</p>
        </div>
        <span className="rounded-full bg-slate-100 px-3 py-1.5 text-sm font-semibold text-slate-600">
          {searchType === "name" ? "Title search" : searchType === "questionId" ? "Question ID search" : "Numeric ID search"}
        </span>
      </div>

      <div className="grid grid-cols-1 gap-3 xl:grid-cols-[1.35fr_1fr_0.85fr_0.75fr_auto]">
        <label className="relative">
          <AiOutlineSearch className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-xl text-slate-400" />
          {searchType === "name" && (
            <input
              type="text"
              placeholder="Search by title, question, or audio script"
              value={searchQuery}
              onChange={handleSearch}
              className="h-11 w-full rounded-lg border border-slate-200 bg-slate-50 pl-10 pr-3 text-sm text-slate-700 outline-none transition focus:border-sky-400 focus:bg-white focus:ring-2 focus:ring-sky-100"
            />
          )}
          {searchType === "questionId" && (
            <input
              type="text"
              placeholder="Search by Question ID"
              value={questionId}
              onChange={(event) => {
                const value = event.target.value.toLowerCase();
                setQuestionId(value);
                setSearchQuery(value);
                handleSearch(event);
              }}
              className="h-11 w-full rounded-lg border border-slate-200 bg-slate-50 pl-10 pr-3 text-sm text-slate-700 outline-none transition focus:border-sky-400 focus:bg-white focus:ring-2 focus:ring-sky-100"
            />
          )}
          {searchType === "id" && (
            <input
              type="text"
              placeholder="Search by ID"
              value={id}
              onChange={(event) => {
                const value = event.target.value;
                setId(value);
                setSearchQuery(value);
                handleSearch(event);
              }}
              className="h-11 w-full rounded-lg border border-slate-200 bg-slate-50 pl-10 pr-3 text-sm text-slate-700 outline-none transition focus:border-sky-400 focus:bg-white focus:ring-2 focus:ring-sky-100"
            />
          )}
        </label>

        <label className="relative">
          <AiOutlineDatabase className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-xl text-slate-400" />
          <select
            value={selectedModel}
            onChange={handleModelChange}
            className="h-11 w-full appearance-none rounded-lg border border-slate-200 bg-slate-50 pl-10 pr-3 text-sm text-slate-700 outline-none transition focus:border-sky-400 focus:bg-white focus:ring-2 focus:ring-sky-100"
          >
            <option value="">All Models</option>
            <optgroup label="Speaking Models">
              <option value="readaloud">Read Aloud</option>
              <option value="repeatsentence">Repeat Sentence</option>
              <option value="describeimage">Describe Image</option>
              <option value="retelllecture">Retell Lecture</option>
              <option value="answershortquestion">Answer Short Question</option>
            </optgroup>
            <optgroup label="Writing Models">
              <option value="summarizetext">Summarize Text</option>
              <option value="writeessay">Write Essay</option>
            </optgroup>
            <optgroup label="Reading Models">
              <option value="singleanswer">Single Answer</option>
              <option value="doubleanswer">Double Answer</option>
              <option value="reorderparagraph">Reorder Paragraph</option>
              <option value="fillintheblank">Fill in the Blank</option>
            </optgroup>
            <optgroup label="Listening Models">
              <option value="summarizespokentext">Summarize Spoken Text</option>
              <option value="writefromdictation">Write from Dictation</option>
              <option value="highlightsummary">Highlight Correct Summary</option>
              <option value="missingword">Select Missing Word</option>
              <option value="incorrectword">Highlight Incorrect Word</option>
            </optgroup>
          </select>
        </label>

        <label className="relative">
          <AiOutlineFilter className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-xl text-slate-400" />
          <select
            value={searchType}
            onChange={(event) => {
              const newSearchType = event.target.value;
              setSearchType(newSearchType);
              if (newSearchType === "name") {
                setQuestionId("");
                setId("");
              } else if (newSearchType === "questionId") {
                setId("");
              } else if (newSearchType === "id") {
                setQuestionId("");
              }
            }}
            className="h-11 w-full appearance-none rounded-lg border border-slate-200 bg-slate-50 pl-10 pr-3 text-sm text-slate-700 outline-none transition focus:border-sky-400 focus:bg-white focus:ring-2 focus:ring-sky-100"
          >
            <option value="name">Title/Question</option>
            <option value="questionId">Question ID</option>
            <option value="id">Numeric ID</option>
          </select>
        </label>

        <input
          type="number"
          value={customLimit}
          onChange={handleCustomLimitChange}
          min={1}
          placeholder="Limit"
          className="h-11 rounded-lg border border-slate-200 bg-slate-50 px-3 text-sm text-slate-700 outline-none transition focus:border-sky-400 focus:bg-white focus:ring-2 focus:ring-sky-100"
          aria-label="Custom limit for items per page"
        />

        <button
          onClick={handleSearchButtonClick}
          className="h-11 rounded-lg bg-sky-600 px-5 text-sm font-semibold text-white shadow-sm transition hover:bg-sky-700 disabled:cursor-not-allowed disabled:opacity-60"
          disabled={busy}
        >
          {busy ? "Searching..." : "Search"}
        </button>
      </div>

      {errorMessage && (
        <div className="mt-4 rounded-lg border border-rose-100 bg-rose-50 px-3 py-2 text-sm font-semibold text-rose-700">
          {errorMessage}
        </div>
      )}
    </section>
  );
};

export default SearchBar;
